import "./Portfolio.css";

import React, { useState, useMemo } from "react";
import PortfolioNavbar from "../portfolio-components/PortfolioNavbar";
import { Helmet } from "react-helmet";
import { useTransactions } from "../../lib/useTransactions";
import {
  computeAllHoldingMetrics,
  allDisposals,
  soldPositions,
  incomeAmounts,
  incomeByYear,
  incomeTransactions,
} from "../../lib/portfolioMetrics";
import { downloadCsv } from "../../lib/csv";

function ExportButton({ filename, headers, rows }) {
  return (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary mb-2"
      onClick={() => downloadCsv(filename, headers, rows)}
      disabled={rows.length === 0}
    >
      <i className="fa-solid fa-download me-1"></i>Export CSV
    </button>
  );
}

function isoDate(tsMs) {
  return tsMs ? new Date(tsMs).toISOString().slice(0, 10) : "";
}

const TITLE = "Reports";

function fmtDate(tsMs) {
  if (!tsMs) return "-";
  return new Date(tsMs).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtNum(val, decimals = 2) {
  if (val === null || val === undefined) return "-";
  return Number(val).toFixed(decimals);
}

function gainClass(val) {
  if (val === null || val === undefined || val === 0) return undefined;
  return val > 0 ? "text-success" : "text-danger";
}

// ── Capital gains report (Sharesight CGT-report style) ───────────────────────

function CapitalGainsTab({ metricsByTicker }) {
  const disposals = useMemo(() => allDisposals(metricsByTicker), [metricsByTicker]);
  const years = [...new Set(disposals.map((d) => new Date(d.timestamp).getFullYear()))].sort((a, b) => b - a);
  const [year, setYear] = useState("all");

  const filtered = year === "all"
    ? disposals
    : disposals.filter((d) => new Date(d.timestamp).getFullYear() === Number(year));

  const totalGain = filtered.reduce((sum, d) => sum + d.gain, 0);

  if (disposals.length === 0) {
    return <p className="text-muted">No disposals found. Sell transactions will appear here once imported.</p>;
  }

  return (
    <div>
      <p className="text-muted">
        Realized gains use first-in-first-out (FIFO) lot matching in the instrument currency.
      </p>
      <div className="d-flex gap-3 align-items-end mb-3">
        <div style={{ maxWidth: 200 }}>
          <label className="form-label" htmlFor="cgt-year">Calendar year</label>
          <select id="cgt-year" className="form-select form-select-sm" value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="all">All years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <ExportButton
          filename={`capital-gains-${year}.csv`}
          headers={["Date Sold", "Ticker", "First Acquired", "Quantity", "Cost Base", "Proceeds", "Gain", "Currency"]}
          rows={filtered.map((d) => [
            isoDate(d.timestamp), d.ticker, isoDate(d.buyDates[0]),
            d.quantity, d.cost, d.proceeds, d.gain, d.currency || "",
          ])}
        />
      </div>
      <table className="table table-sm">
        <thead>
          <tr>
            <th>Date Sold</th>
            <th>Ticker</th>
            <th>First Acquired</th>
            <th>Quantity</th>
            <th>Cost Base</th>
            <th>Proceeds</th>
            <th>Gain / Loss</th>
            <th>Currency</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((d, i) => (
            <tr key={`${d.ticker}-${d.timestamp}-${i}`}>
              <td>{fmtDate(d.timestamp)}</td>
              <td>{d.ticker}</td>
              <td>{fmtDate(d.buyDates[0])}</td>
              <td>{fmtNum(d.quantity, 4)}</td>
              <td>{fmtNum(d.cost)}</td>
              <td>{fmtNum(d.proceeds)}</td>
              <td className={gainClass(d.gain)}>{fmtNum(d.gain)}</td>
              <td>{d.currency || "-"}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="fw-bold">
            <td colSpan={6}>Total realized gain</td>
            <td className={gainClass(totalGain)}>{fmtNum(totalGain)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── Taxable income report ─────────────────────────────────────────────────────

function IncomeTab({ transactions }) {
  const years = useMemo(() => incomeByYear(transactions), [transactions]);
  const detail = useMemo(() => incomeTransactions(transactions), [transactions]);

  if (years.length === 0) {
    return <p className="text-muted">No dividend or interest transactions found. Import a broker CSV to populate this report.</p>;
  }

  return (
    <div>
      <h5>Income by Year</h5>
      <p className="text-muted">Broker-reported dividend and interest payments, in your account currency.</p>
      <table className="table table-sm" style={{ maxWidth: 600 }}>
        <thead>
          <tr>
            <th>Year</th>
            <th>Payments</th>
            <th>Gross</th>
            <th>Tax Withheld</th>
            <th>Net</th>
          </tr>
        </thead>
        <tbody>
          {years.map((y) => (
            <tr key={y.year}>
              <td>{y.year}</td>
              <td>{y.count}</td>
              <td>{fmtNum(y.gross)}</td>
              <td>{fmtNum(y.taxWithheld)}</td>
              <td className="text-success">{fmtNum(y.net)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h5 className="mt-4">All Income Payments</h5>
      <ExportButton
        filename="income.csv"
        headers={["Date", "Type", "Ticker", "Per Share", "Eligible Qty", "Gross", "Net", "Tax Withheld"]}
        rows={detail.map((tx) => {
          const { gross, net } = incomeAmounts(tx);
          return [
            isoDate(tx.timestamp), tx.type, tx.ticker || "",
            tx.dividendAmountPerShare ?? "", tx.dividendEligibleQuantity ?? "",
            gross ?? "", net ?? "",
            tx.dividendWithheldTaxAmount ?? "",
          ];
        })}
      />
      <table className="table table-sm">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Ticker</th>
            <th>Per Share</th>
            <th>Eligible Qty</th>
            <th>Gross</th>
            <th>Net</th>
          </tr>
        </thead>
        <tbody>
          {detail.map((tx, i) => {
            const { gross, net } = incomeAmounts(tx);
            return (
              <tr key={i}>
                <td>{fmtDate(tx.timestamp)}</td>
                <td>{tx.type}</td>
                <td>{tx.ticker || "—"}</td>
                <td>{fmtNum(tx.dividendAmountPerShare, 4)}</td>
                <td>{fmtNum(tx.dividendEligibleQuantity, 4)}</td>
                <td>{fmtNum(gross)}</td>
                <td>{fmtNum(net)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Sold (historical) positions ───────────────────────────────────────────────

function SoldPositionsTab({ metricsByTicker }) {
  const sold = useMemo(() => soldPositions(metricsByTicker), [metricsByTicker]);

  if (sold.length === 0) {
    return <p className="text-muted">No fully sold positions yet.</p>;
  }

  return (
    <div>
    <ExportButton
      filename="sold-positions.csv"
      headers={["Ticker", "Last Sold", "Total Invested", "Total Proceeds", "Realized Gain", "Currency"]}
      rows={sold.map((p) => [
        p.ticker, isoDate(p.lastSoldTs), p.buyCost, p.sellProceeds, p.realizedGain, p.currency || "",
      ])}
    />
    <table className="table table-sm">
      <thead>
        <tr>
          <th>Ticker</th>
          <th>Last Sold</th>
          <th>Total Invested</th>
          <th>Total Proceeds</th>
          <th>Realized Gain</th>
          <th>Currency</th>
        </tr>
      </thead>
      <tbody>
        {sold.map((p) => (
          <tr key={p.ticker}>
            <td>{p.ticker}</td>
            <td>{fmtDate(p.lastSoldTs)}</td>
            <td>{fmtNum(p.buyCost)}</td>
            <td>{fmtNum(p.sellProceeds)}</td>
            <td className={gainClass(p.realizedGain)}>{fmtNum(p.realizedGain)}</td>
            <td>{p.currency || "-"}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

// ── All trades ────────────────────────────────────────────────────────────────

function AllTradesTab({ transactions }) {
  const trades = useMemo(
    () => (transactions || [])
      .filter((tx) => tx.type === "ORDER")
      .slice()
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)),
    [transactions]
  );

  if (trades.length === 0) {
    return <p className="text-muted">No trades found. Import a broker CSV to populate this report.</p>;
  }

  return (
    <div>
    <ExportButton
      filename="trades.csv"
      headers={["Date", "Ticker", "Direction", "Quantity", "Price Per Share", "Total", "Currency"]}
      rows={trades.map((tx) => [
        isoDate(tx.timestamp), tx.ticker || "", tx.buySell || "",
        tx.quantity ?? "", tx.pricePerShare ?? "", tx.totalAmount ?? "",
        tx.accountCurrency || tx.instrumentCurrency || "",
      ])}
    />
    <table className="table table-sm">
      <thead>
        <tr>
          <th>Date</th>
          <th>Ticker</th>
          <th>Direction</th>
          <th>Quantity</th>
          <th>Price/Share</th>
          <th>Total</th>
          <th>Currency</th>
        </tr>
      </thead>
      <tbody>
        {trades.map((tx, i) => (
          <tr key={i}>
            <td>{fmtDate(tx.timestamp)}</td>
            <td>{tx.ticker || "-"}</td>
            <td className={tx.buySell === "BUY" ? "text-success" : "text-danger"}>{tx.buySell || "-"}</td>
            <td>{fmtNum(tx.quantity, 4)}</td>
            <td>{fmtNum(tx.pricePerShare, 4)}</td>
            <td>{fmtNum(tx.totalAmount)}</td>
            <td>{tx.accountCurrency || tx.instrumentCurrency || "-"}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const TABS = [
  { key: "capitalGains", label: "Capital Gains" },
  { key: "income", label: "Taxable Income" },
  { key: "sold", label: "Sold Positions" },
  { key: "trades", label: "All Trades" },
];

function ReportsView() {
  const [tab, setTab] = useState("capitalGains");
  const { transactions, loading, error } = useTransactions();

  const metricsByTicker = useMemo(
    () => (transactions ? computeAllHoldingMetrics(transactions) : {}),
    [transactions]
  );

  return (
    <div className="ReportsView">
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>
      <div className="row">
        <div className="sidebar col-md-auto">
          <PortfolioNavbar />
        </div>

        <div className="main-content col">
          <h2>Reports</h2>

          {loading && <p>Loading transaction history...</p>}
          {error && <p className="text-danger">Error: {error.message}</p>}

          {!loading && !error && (
            <>
              <ul className="nav nav-tabs mb-3">
                {TABS.map((t) => (
                  <li className="nav-item" key={t.key}>
                    <button
                      className={`nav-link${tab === t.key ? " active" : ""}`}
                      onClick={() => setTab(t.key)}
                    >
                      {t.label}
                    </button>
                  </li>
                ))}
              </ul>

              {tab === "capitalGains" && <CapitalGainsTab metricsByTicker={metricsByTicker} />}
              {tab === "income" && <IncomeTab transactions={transactions} />}
              {tab === "sold" && <SoldPositionsTab metricsByTicker={metricsByTicker} />}
              {tab === "trades" && <AllTradesTab transactions={transactions} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportsView;
